<template>
    <div class="page-content">
        <h1>{{ letterTitle }}</h1>

        <div v-if="isLoading" class="preloader">
            <img src="assets/ajax-loader.gif" />
        </div>
        <b-card v-else footer-class="p-0" :key="cardKey">
            <letter-display
                ref="letterDisplay"
                :mode="profileMode"
                :letter="patientLetterDetails"
                @edit-saved="editSaved"
            />
            <template v-slot:footer no-body>
                <b-tabs card lazy class="list" v-if="patientLetterDetails">
                    <tab-letter
                        tabname="tabTemplate"
                        :letterDetails="patientLetterDetails"
                        :isLetterLocked="isLetterLocked"
                        @content-saved="contentSaved"
                        @detail-saved="editSaved"
                        @letter-status="$emit('letter-status', $event)"
                    />
                </b-tabs>
            </template>
        </b-card>
        <modal-email-letter ref="modalEmailLetter" />
        <modal-delete-document
            ref="modalDeleteDocument"
            @on-success="afterDeleted"
        />
        <lock-letter ref="lockLetter" @after-lock="afterLock" />
        <sign-letter ref="signLetter" @on-success="reloadPageDetails" />
        <modal-add-attachment
            v-if="patientLetterDetails"
            :patientLetter="patientLetterDetails"
            @reload="successUpdateAttachment"
            ref="modalAddAttachment"
        />
    </div>
</template>

<script>
import LetterDisplay from "@/components/patient-letter/letter-display";
import LockLetter from "@/components/patient-letter/letter-lock";
import SignLetter from "@/components/patient-letter/letter-sign";
import TabLetter from "./tabs/tab-letter";
import ModalEmailLetter from "@/components/patient-documents/document-email-letter-modal.vue";
import ModalDeleteDocument from "@/components/patient-documents/document-delete-modal.vue";
import unsavePrompt from "@/mixins/unsave-prompt.js";
import PatientDocument from "@/services/patient-documents";
import ModalAddAttachment from "@/components/patient-letter/modal-link-attachment";

export default {
    mixins: [unsavePrompt],
    components: {
        LetterDisplay,
        LockLetter,
        SignLetter,
        TabLetter,
        ModalEmailLetter,
        ModalDeleteDocument,
        ModalAddAttachment
    },
    props: {
        profileMode: {
            type: String
        }
    },
    data() {
        return {
            patientLetterDetails: null,
            patientLetterContent: null,
            isLetterLocked: false,
            isLoading: false,
            cardKey: 0
        };
    },
    computed: {
        letterId() {
            return this.patientLetterDetails
                ? this.patientLetterDetails.id
                : null;
        },
        letterTitle() {
            if (this.isLoading) {
                return "Loading...";
            }
            return this.patientLetterDetails
                ? this.patientLetterDetails.title
                : "";
        },
        routeParam() {
            return this.$route.params.id;
        }
    },
    watch: {
        routeParam() {
            this.reloadPageDetails();
            this.cardKey++;
        }
    },
    mounted() {
        this.reloadPageDetails();
    },
    sockets: {},
    destroyed() {},
    methods: {
        async reloadPageDetails() {
            await this.getDetails();
            await this.getContent();
        },
        async getDetails() {
            var encodedId = this.$route.params.id;

            this.$store.commit("patient/setPatientLetterDetails", null);

            if (encodedId) {
                await this.$store
                    .dispatch("patient/getPatientLetterDetails", {
                        id: encodedId,
                        encoded: this.$route.query.encoded === "0" ? 0 : 1
                    })
                    .then(hasData => {
                        if (hasData) {
                            this.patientLetterDetails = hasData;
                            this.isLetterLocked = this.patientLetterDetails
                                .lock_status
                                ? true
                                : false;

                            this.$store.commit(
                                "patient/setPatientLetterDetails",
                                hasData
                            );
                            this.$emit("letter-status", {
                                isLock: this.isLetterLocked,
                                hasSignature: this.patientLetterDetails
                                    .has_signature
                                    ? true
                                    : false,
                                hasSigned:
                                    !this.patientLetterDetails.signature ||
                                    this.patientLetterDetails.signature == ""
                                        ? false
                                        : true
                            });
                        }
                    });
            } else {
                this.$router.push({ name: "patient_profile" });
            }
        },
        async getContent() {
            var self = this;
            var encodedId = this.$route.params.id;

            this.$store.commit("patient/setPatientLetterContent", null);

            if (encodedId) {
                await this.$store
                    .dispatch("patient/getPatientLetterContent", {
                        id: encodedId,
                        encoded: this.$route.query.encoded === "0" ? 0 : 1
                    })
                    .then(hasData => {
                        if (hasData) {
                            this.patientLetterContent = hasData;
                            this.$store.commit(
                                "patient/setPatientLetterContent",
                                hasData
                            );
                        }
                    });
            }
        },
        saveEditLetter() {
            this.$refs.letterDisplay.saveEdit();
        },
        emailLetter() {
            this.$refs.modalEmailLetter.open(this.patientLetterDetails);
        },
        async printLetter() {
            const pdfPath = await PatientDocument.getPDFLetterURI(
                this.letterId
            );
            if (pdfPath) {
                window.open(pdfPath, "_blank");
            } else {
                this._message("Error: Unable to print", "danger");
            }
        },
        contentSaved() {
            this.reloadPageDetails();
        },
        editSaved(details) {
            this.$emit("edit-saved", details);
            this.getDetails();
            if (!details.isAttachmentAdd) {
                this.getContent();
            }
        },
        promptDelete(type) {
            this.$refs.modalDeleteDocument.open({
                type: type,
                id: this.letterId
            });
        },
        promptLock() {
            this.$refs.lockLetter.open(this.letterId);
        },
        promptSign() {
            this.$refs.signLetter.open(this.patientLetterDetails);
        },
        promptAddAttachment() {
            this.$refs.modalAddAttachment.open(this.patientLetterDetails);
        },
        successUpdateAttachment() {
            this.getDetails();
            // reloadPageDetails
        },
        clearSigniture() {
            let data = new FormData();
            data.append("id", this.patientLetterDetails.id);
            data.append("referrer", "letter");
            PatientDocument.clearSigniture(data).then(response => {
                if (response.state == 0) {
                    this.reloadPageDetails();
                    this._message(response.message);
                } else {
                    this._message(response.message, "danger");
                }
            });
        },
        afterLock(result = {}) {
            if (result.isLock) {
                this.isLetterLocked = true;
                this.$emit("letter-status", {
                    isLock: true
                });
            }
        },
        afterDeleted() {
            if (this.$route.name == "client_letter") {
                this.$router.push({
                    name: "client_profile",
                    params: {
                        id: this.patientLetterDetails.client.encoded_id
                    }
                });
            } else {
                this.$router.push({
                    name: "patient_profile",
                    params: {
                        id: this.patientLetterDetails.patient.encoded_id
                    }
                });
            }
        }
    }
};
</script>

<style scoped>
.preloader {
    margin-left: 15px;
}
</style>
